import React, { useEffect, useRef } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    Box,
    Text,
} from '@chakra-ui/react';

interface Entity {
    text: string;
    score_dictionary: Record<string, number>;
    additional_metadata: {
        page_number: string;
        start_offset: string;
        length: string;
    };
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    documents: string[];
    allEntities: Entity[];
    startPage?: string;
}

const PiiDocumentModal: React.FC<Props> = ({ isOpen, onClose, documents, allEntities, startPage }) => {
    const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        if (!isOpen || startPage === undefined) return;

        const index = parseInt(startPage, 10) - 1;
        const waitForElement = () => {
            const pageElement = pageRefs.current[index];
            if (pageElement) {
                pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                requestAnimationFrame(waitForElement);
            }
        };

        requestAnimationFrame(waitForElement);
    }, [startPage, isOpen]);

    const highlightWords = (text: string, pageIndex: number) => {
        const entities = allEntities.filter(
            (e) => parseInt(e.additional_metadata.page_number, 10) === pageIndex + 1
        );

        if (entities.length === 0) return [text];

        const sortedEntities = [...entities]
            .map((e) => {
                const index = text.indexOf(e.text);
                if (index === -1) return null;
                return {
                    ...e,
                    start: index,
                    end: index + e.text.length,
                };
            })
            .filter((e): e is any => e !== null)
            .sort((a, b) => a.start - b.start);

        const fragments: React.ReactNode[] = [];
        let lastIndex = 0;

        sortedEntities.forEach((entity: any, i) => {
            if (entity.start > lastIndex) {
                fragments.push(
                    <span key={`text-${i}`}>{text.slice(lastIndex, entity.start)}</span>
                );
            }
            fragments.push(
                <mark
                    key={`mark-${i}`}
                    style={{ backgroundColor: 'yellow', padding: '0 2px', borderRadius: '3px' }}
                >
                    {text.slice(entity.start, entity.end)}
                </mark>
            );
            lastIndex = entity.end;
        });
        if (lastIndex < text.length) {
            fragments.push(<span key="last">{text.slice(lastIndex)}</span>);
        }

        return fragments;
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent maxHeight="90vh">
                <ModalHeader>Document with PII Highlights</ModalHeader>
                <ModalCloseButton />
                <ModalBody overflowY="auto">
                    {documents.map((doc, idx) => (
                        <Box
                            key={idx}
                            ref={(el) => (pageRefs.current[idx] = el)}
                            p={4}
                            mb={4}
                            borderWidth="1px"
                            borderRadius="md"
                            boxShadow="sm"
                            bg="gray.50"
                        >
                            <Text fontWeight="bold" mb={2}>Page {idx + 1}</Text>
                            <Text whiteSpace="pre-wrap" fontSize="md" lineHeight="1.6">
                                {highlightWords(doc, idx)}
                            </Text>
                        </Box>
                    ))}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default PiiDocumentModal;
